// src/pages/vendor/KYC.jsx - FIXED URL RESOLUTION
import React, { useState, useEffect } from 'react';
import { vendorApi } from '../../api/vendorApi';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
  Link,
  useMediaQuery,
  useTheme,
  Avatar,
} from '@mui/material';
import { 
  CloudUpload, 
  CheckCircle, 
  Verified, 
  FilePresent, 
  Refresh,
  Business,
  Receipt,
  Warning,
  Info,
} from '@mui/icons-material';

// ✅ FIX: Correct API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL 
  ? process.env.REACT_APP_API_URL.replace(/\/api$/, '') 
  : 'https://api.vtech-solution.com';

// ✅ FIX: Helper function to resolve document URLs correctly
const resolveDocumentUrl = (url) => {
  if (!url) return null;
  
  // If it's already a full URL with https:// or http://
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // Clean the URL - remove any double slashes or incorrect prefixes
  let cleanUrl = url;
  
  // Remove any leading .vtech-solution.com or similar from the URL
  cleanUrl = cleanUrl.replace(/^\.vtech-solution\.com/i, '');
  cleanUrl = cleanUrl.replace(/^\.vtech-solution\.com\/api/i, '');
  cleanUrl = cleanUrl.replace(/^\/\/vtech-solution\.com/i, '');
  cleanUrl = cleanUrl.replace(/^vtech-solution\.com/i, '');
  
  // Ensure the URL starts with /uploads
  if (!cleanUrl.startsWith('/uploads') && !cleanUrl.startsWith('uploads')) {
    // If it doesn't start with /uploads, check if it has uploads in the path
    const uploadsIndex = cleanUrl.indexOf('/uploads');
    if (uploadsIndex !== -1) {
      cleanUrl = cleanUrl.substring(uploadsIndex);
    } else {
      // If no uploads path found, assume it's a direct file path
      cleanUrl = cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`;
    }
  }
  
  // Ensure the URL starts with /
  if (!cleanUrl.startsWith('/')) {
    cleanUrl = `/${cleanUrl}`;
  }
  
  // Remove any duplicate /api/ in the path
  cleanUrl = cleanUrl.replace(/\/api\/uploads/g, '/uploads');
  cleanUrl = cleanUrl.replace(/\/api\/kyc/g, '/kyc');
  
  // Final URL construction
  const finalUrl = `${API_BASE_URL}${cleanUrl}`;
  
  return finalUrl;
};

const KYC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [kycData, setKycData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  const fetchKYCStatus = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const res = await vendorApi.getKYCStatus();
      
      // ✅ FIX: Resolve document URLs
      const licenseUrl = res?.licenseUrl ? resolveDocumentUrl(res.licenseUrl) : null;
      const taxIdUrl = res?.taxIdUrl ? resolveDocumentUrl(res.taxIdUrl) : null;
      
      setKycData({
        isVerified: res?.isVerified || false,
        storeName: res?.storeName || 'Vendor',
        licenseUrl: licenseUrl,
        taxIdUrl: taxIdUrl,
        hasLicense: !!licenseUrl,
        hasTaxId: !!taxIdUrl,
        submittedAt: res?.submittedAt || res?.createdAt || null,
        taxId: res?.taxId || null,
        businessEmail: res?.businessEmail || null,
        businessPhone: res?.businessPhone || null,
        businessAddress: res?.businessAddress || null,
        isActive: res?.isActive || false,
      });
    } catch (err) {
      console.error('Error fetching KYC status:', err);
      setKycData({
        isVerified: false,
        storeName: 'Vendor',
        licenseUrl: null,
        taxIdUrl: null,
        hasLicense: false,
        hasTaxId: false,
        submittedAt: null,
        taxId: null,
        businessEmail: null,
        businessPhone: null,
        businessAddress: null,
        isActive: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const handleUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setMessage(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    try {
      const response = await vendorApi.uploadKycDocument(formData);
      
      // ✅ FIX: Resolve the returned document URL
      let updatedLicenseUrl = kycData?.licenseUrl;
      let updatedTaxIdUrl = kycData?.taxIdUrl;
      
      if (type === 'license' && response?.licenseUrl) {
        updatedLicenseUrl = resolveDocumentUrl(response.licenseUrl);
      } else if (type === 'taxId' && response?.taxIdUrl) {
        updatedTaxIdUrl = resolveDocumentUrl(response.taxIdUrl);
      }
      
      setKycData(prev => ({
        ...prev,
        licenseUrl: type === 'license' ? updatedLicenseUrl : prev?.licenseUrl,
        taxIdUrl: type === 'taxId' ? updatedTaxIdUrl : prev?.taxIdUrl,
        hasLicense: type === 'license' ? true : prev?.hasLicense,
        hasTaxId: type === 'taxId' ? true : prev?.hasTaxId,
        submittedAt: new Date().toISOString(),
      }));
      
      setMessage({ 
        type: 'success', 
        text: `${type === 'license' ? 'Business License' : 'Tax ID'} uploaded successfully! Admin will review your new documents shortly.` 
      });
      
      await fetchKYCStatus();
    } catch (err) {
      console.error('Upload error:', err);
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.message || 'Upload failed. Please try again.' 
      });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }} 
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              KYC & Verification
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your business documents and track approval status
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchKYCStatus}
            size={isMobile ? 'small' : 'medium'}
            fullWidth={isMobile}
          >
            Refresh Status
          </Button>
        </Stack>
      </Paper>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 2, borderRadius: 2 }}
          icon={message.type === 'success' ? <CheckCircle /> : <Warning />}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Status Card */}
        <Grid item xs={12}>
          <Card 
            sx={{ 
              bgcolor: kycData?.isVerified ? 'success.light' : kycData?.isActive === false ? 'error.light' : 'warning.light',
              borderRadius: 3,
              border: `1px solid ${kycData?.isVerified ? '#4caf50' : kycData?.isActive === false ? '#f44336' : '#ff9800'}`,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                alignItems={{ xs: 'flex-start', sm: 'center' }} 
                spacing={2}
              >
                <Avatar
                  sx={{
                    width: { xs: 48, sm: 56 },
                    height: { xs: 48, sm: 56 },
                    bgcolor: kycData?.isVerified ? 'success.main' : kycData?.isActive === false ? 'error.main' : 'warning.main',
                  }}
                >
                  {kycData?.isVerified ? (
                    <CheckCircle sx={{ fontSize: { xs: 28, sm: 32 }, color: 'white' }} />
                  ) : kycData?.isActive === false ? (
                    <Warning sx={{ fontSize: { xs: 28, sm: 32 }, color: 'white' }} />
                  ) : (
                    <Verified sx={{ fontSize: { xs: 28, sm: 32 }, color: 'white' }} />
                  )}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {kycData?.isVerified 
                      ? '✅ Verified' 
                      : kycData?.isActive === false 
                        ? '❌ Rejected' 
                        : '⏳ Pending Review'
                    }
                  </Typography>
                  <Typography variant="body2">
                    {kycData?.isVerified
                      ? 'Your documents have been approved. You are fully verified!'
                      : kycData?.isActive === false
                        ? 'Your documents have been rejected. Please upload new documents for review.'
                        : 'Your documents are under review by the Admin team. Please wait for approval.'
                    }
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Store: {kycData?.storeName || 'Vendor'}
                  </Typography>
                  {kycData?.submittedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Submitted: {new Date(kycData.submittedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Business License Upload */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Business sx={{ color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Business License
                  </Typography>
                </Stack>
                <Divider />
                
                {kycData?.hasLicense || kycData?.licenseUrl ? (
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip 
                        icon={<FilePresent />} 
                        label="Uploaded" 
                        color="success" 
                        variant="outlined"
                        size={isMobile ? 'small' : 'medium'}
                      />
                      {kycData?.isVerified && (
                        <Chip 
                          label="Approved" 
                          color="success" 
                          size="small"
                        />
                      )}
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                      <Link 
                        href={kycData.licenseUrl} 
                        target="_blank" 
                        underline="hover"
                        sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}
                      >
                        View Document →
                      </Link>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      * Uploading a new file will replace the old one and require re-approval.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <FilePresent sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No license uploaded yet.
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="contained"
                  component="label"
                  startIcon={kycData?.hasLicense ? <Refresh /> : <CloudUpload />}
                  disabled={uploading}
                  fullWidth={isMobile}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  {uploading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : kycData?.hasLicense ? (
                    'Upload Renewed License'
                  ) : (
                    'Upload License'
                  )}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.png,.jpeg"
                    onChange={(e) => handleUpload(e, 'license')}
                  />
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Tax ID Upload */}
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Receipt sx={{ color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Tax ID / TIN
                  </Typography>
                </Stack>
                <Divider />
                
                {kycData?.hasTaxId || kycData?.taxIdUrl ? (
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip 
                        icon={<FilePresent />} 
                        label="Uploaded" 
                        color="success" 
                        variant="outlined"
                        size={isMobile ? 'small' : 'medium'}
                      />
                      {kycData?.isVerified && (
                        <Chip 
                          label="Approved" 
                          color="success" 
                          size="small"
                        />
                      )}
                    </Stack>
                    <Box sx={{ mt: 1 }}>
                      <Link 
                        href={kycData.taxIdUrl} 
                        target="_blank" 
                        underline="hover"
                        sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}
                      >
                        View Document →
                      </Link>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      * Uploading a new file will replace the old one and require re-approval.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Receipt sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No Tax ID uploaded yet.
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="contained"
                  component="label"
                  startIcon={kycData?.hasTaxId ? <Refresh /> : <CloudUpload />}
                  disabled={uploading}
                  fullWidth={isMobile}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  {uploading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : kycData?.hasTaxId ? (
                    'Upload Renewed Tax ID'
                  ) : (
                    'Upload Tax ID'
                  )}
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.png,.jpeg"
                    onChange={(e) => handleUpload(e, 'taxId')}
                  />
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Tax ID Number Display */}
        {kycData?.taxId && (
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, bgcolor: 'info.light' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Info sx={{ color: 'info.main' }} />
                <Box>
                  <Typography variant="body2" fontWeight="600">
                    Tax ID Number
                  </Typography>
                  <Typography variant="body2">
                    {kycData.taxId}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        )}

        {/* Info Note */}
        <Grid item xs={12}>
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, bgcolor: 'info.light' }}>
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Info sx={{ color: 'info.main', mt: 0.5 }} />
              <Box>
                <Typography variant="body2" fontWeight="600">
                  What happens next?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Once you upload your documents, the Admin team will review them and update your verification status. 
                  This usually takes 24-48 hours. You'll be notified once your account is verified.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  ✅ <strong>Accepted formats:</strong> PDF, JPG, PNG (max 5MB per file)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  📄 <strong>Required documents:</strong> Business License and Tax ID / TIN Certificate
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default KYC;